import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { Box, Button, Skeleton, IconButton, Menu, MenuItem, FormControl, InputLabel } from '@mui/material'
import { useQuery } from 'react-query'
import { TextInput, useListContext, useTranslate, BooleanInput, DateTimeInput } from 'react-admin'
import ContentFilter from '@mui/icons-material/FilterList'
import isEqual from 'lodash/isEqual'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { HighlightOff } from '@mui/icons-material'
import { workUnitServices } from 'src/services/reservation/workUnit/WorkUnit.service'
import { TreeViewInput } from 'src/components/uiElements/CRUD/TreeViewLandingAndBanner'
import { gatewayServices } from 'src/services/payment/gateway/gateway.service'
import { cardServices } from 'src/services/payment/card/card.service'
import { companyServices } from 'src/services/payment/company/company.service'

export const PostFilterButtonCommerceNumber = ({ listFilters }) => {
  const { displayedFilters = {}, setFilters, showFilter } = useListContext()
  const translate = useTranslate()
  const hasDisplayedFilters = !isEqual(displayedFilters, {})
  const [open, setOpen] = useState(false)
  const anchorEl = useRef()

  const handleClickButton = useCallback(
    (event) => {
      // This prevents ghost click.
      event.preventDefault()
      setOpen(true)
      anchorEl.current = event.currentTarget
    },
    [anchorEl, setOpen]
  )

  const handleRequestClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  const handleShow = useCallback(
    ({ source, defaultValue }) => {
      showFilter(source, defaultValue === '' ? undefined : defaultValue)
      setOpen(false)
    },
    [showFilter, setOpen]
  )

  return (
    <Box>
      <Button
        className='add-filter'
        label='ra.action.add_filter'
        aria-haspopup='true'
        onClick={handleClickButton}
        size='small'
      >
        <ContentFilter /> {translate('ra.action.add_filter')}
      </Button>
      <Menu open={open} anchorEl={anchorEl.current} onClose={handleRequestClose}>
        {Array.isArray(listFilters) &&
          listFilters.map(
            (item, index) =>
              !displayedFilters?.[item.source] && (
                <MenuItem
                  onClick={() => {
                    setOpen(false)
                    handleShow({ source: item.source })
                  }}
                  key={index}
                >
                  {translate(`${item.name}`)}
                </MenuItem>
              )
          )}

        {hasDisplayedFilters && (
          <MenuItem onClick={() => setFilters({}, {}, false)}>
            {translate('ra.action.remove_all_filters')}
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

const SelectArrayInputFn = ({
  queryName,
  label,
  optionsText = ['long_name'],
  optionValue,
  source,
  queryFn,
  ...props
}) => {
  const options = useQuery(queryName, () => queryFn())
  return options.isLoading ? (
    <Skeleton variant='rectangular' width={'270px'} height={'40px'} />
  ) : (
    <FormControl fullWidth className='select-array-input'>
      {label && <InputLabel>{label}</InputLabel>}
      <SelectInput
        source={source}
        options={options?.data || []}
        optionsText={optionsText}
        optionValue={optionValue}
        multiple
        showSearch
        {...props}
      />
    </FormControl>
  )
}

const RemoveFilterField = ({ children, prop, fields, width = null, maxWidth = null, minWidth = null }) => {
  const { displayedFilters = {}, setFilters } = useListContext()
  const { setValue, getValues } = useFormContext()

  const onClickButton = () => {
    const copy = { ...displayedFilters }
    delete copy[prop]

    if (Array.isArray(fields) && fields.length > 0) {
      fields.forEach((field) => setValue(field, undefined))

      let values = getValues()
      setFilters(values, copy, false)
    }
  }

  return (
    <Box
      display={'flex'}
      alignItems={'flex-start'}
      marginRight={5}
      width={width}
      maxWidth={maxWidth}
      minWidth={minWidth}
    >
      <Box width={'34px'} paddingTop={1}>
        <IconButton size='small' onClick={onClickButton}>
          <HighlightOff />
        </IconButton>
      </Box>
      <Box width={'calc(100% - 34px)'} display={'flex'} minHeight={'60px'} height={'auto'}>
        {children}
      </Box>
    </Box>
  )
}

export const PostFilterFormCommerceNumber = () => {
  const { displayedFilters = {}, filterValues, setFilters } = useListContext()
  const translate = useTranslate()
  const form = useForm()

  useEffect(() => {
    Object.entries(filterValues).forEach((entry) => {
      const [key, value] = entry
      form.setValue(key, value)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (displayedFilters && Object.keys(displayedFilters).length === 0) {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedFilters])

  const onSubmit = (values) => {
    if (Object.keys(values).length > 0) {
      setFilters(values, displayedFilters, false)
    }
  }

  const resetFilter = () => {
    setFilters({}, {}, false)
  }

  const areFilterActivate = (displayedFilters) => {
    if (displayedFilters && Object.keys(displayedFilters).length > 0) {
      const props = Object.values(displayedFilters)
      return props.some((prop) => prop === true)
    }
    return false
  }


  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center' mb={1} height={'auto'}>
          <Box width={'calc(100% - 156px)'} display={'flex'} flexWrap={'wrap'}>
            {displayedFilters?.id && (
              <RemoveFilterField prop={'id'} fields={['id']} width={300}>
                <TextInput label={translate('ra.field.id')} fullWidth source='id' />
              </RemoveFilterField>
            )}
            {displayedFilters?.value && (
              <RemoveFilterField prop={'value'} fields={['value']} width={300}>
                <TextInput label={translate('ra.field.commerce_number')} fullWidth source='value' />
              </RemoveFilterField>
            )}
            {displayedFilters?.terminal && (
              <RemoveFilterField prop={'terminal'} fields={['terminal']} width={300}>
                <TextInput label={translate('ra.field.terminal')} fullWidth source='terminal' />
              </RemoveFilterField>
            )}
            {displayedFilters?.gateway_id && (
              <RemoveFilterField prop={'gateway_id'} fields={['gateway_id']} width={300}>
                <SelectArrayInputFn
                  source='gateway_id'
                  queryName='gateway-all'
                  label={translate('ra.field.manager_responsible')}
                  queryFn={() => gatewayServices.getByAll({ limit: 999 })}
                  optionsText={['name']}
                  separator=' '
                  multiple={false}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.card_id && (
              <RemoveFilterField prop={'card_id'} fields={['card_id']} width={300}>
                <SelectArrayInputFn
                  source='card_id'
                  queryName='card-all'
                  label={translate('ra.field.card')}
                  queryFn={() => cardServices.getByAll({ limit: 999 })}
                  optionsText={['short_name', 'long_name']}
                  separator=' '
                  multiple={false}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.company_id && (
              <RemoveFilterField prop={'company_id'} fields={['company_id']} width={300}>
                <SelectArrayInputFn
                  source='company_id'
                  queryName='company-all'
                  label={translate('ra.field.company')}
                  queryFn={() => companyServices.getByAll({ limit: 999 })}
                  optionsText={['short_name', 'long_name']}
                  separator=' '
                  multiple={false}
                />
              </RemoveFilterField>
            )}
            {displayedFilters?.work_unit_id__in && (
              <RemoveFilterField
                prop={'work_unit_id__in'}
                fields={['work_unit_id__in']}
                minWidth={300}
                maxWidth={600}
              >
                <TreeViewInput
                  queryName='work-unit-all'
                  fieldName='work_unit_id__in'
                  modalTitle={translate('ra.title.select_work_unit')}
                  buttonText={translate('ra.message.filter_by_work_unit')}
                  labelField={['label']}
                  queryFn={workUnitServices.getByOrderParent}
                />
              </RemoveFilterField>
            )}
          </Box>
          <Box
            width={'156px'}
            display={'flex'}
            flexWrap={'wrap'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
          >
            {areFilterActivate(displayedFilters) && (
              <>
                <Box mr={2} paddingY={'10px'}>
                  <Button variant='outlined' color='primary' type='submit'>
                    {translate('ra.action.filter')}
                  </Button>
                </Box>
                <Box paddingY={'10px'}>
                  <Button variant='outlined' onClick={resetFilter}>
                    {translate('ra.action.close')}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </form>
    </FormProvider>
  )
}
